import React from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./EliminatingEntryDrawer.module.scss";

export type EliminatingEntrySubmitMode = "save" | "save-and-new";

interface Props {
    canSubmit: boolean;
    isPending: boolean;
    isEditing: boolean;
    isLocked?: boolean;
    validationMessage: string;
    mutationError: string | null;
    pendingSubmitMode?: EliminatingEntrySubmitMode | null;
    onAddRows: () => void;
    onSubmit: (mode: EliminatingEntrySubmitMode) => void;
    onDelete: () => void;
}

export const EliminatingEntryFormFooter: React.FC<Props> = ({
    canSubmit,
    isPending,
    isEditing,
    isLocked = false,
    validationMessage,
    mutationError,
    pendingSubmitMode,
    onAddRows,
    onSubmit,
    onDelete,
}) => {
    if (isLocked) {
        return null;
    }

    return (
        <HStack
            gap={12}
            justify="between"
            align="center"
            padding={16}
            className={styles.footer}
        >
            <HStack gap={12}>
                <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    disabled={isPending}
                    onClick={onAddRows}
                >
                    + 20 rows
                </Button>
                {isEditing && (
                    <Button
                        type="button"
                        variant="destructive"
                        size="md"
                        disabled={isPending}
                        onClick={onDelete}
                    >
                        Delete
                    </Button>
                )}
            </HStack>
            <HStack gap={12} align="center">
                <div
                    className={styles.footerMessages}
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    {validationMessage && (
                        <p className={styles.footerError}>
                            {validationMessage}
                        </p>
                    )}
                    {mutationError && (
                        <p className={styles.footerError}>{mutationError}</p>
                    )}
                </div>
                {!isEditing && (
                    <ButtonWithLoader
                        type="button"
                        variant="primary"
                        size="md"
                        loading={
                            isPending && pendingSubmitMode === "save-and-new"
                        }
                        disabled={!canSubmit || isPending}
                        onClick={() => onSubmit("save-and-new")}
                    >
                        Save and new
                    </ButtonWithLoader>
                )}
                <ButtonWithLoader
                    type="button"
                    variant="primary"
                    size="md"
                    loading={isPending && pendingSubmitMode === "save"}
                    disabled={!canSubmit || isPending}
                    onClick={() => onSubmit("save")}
                    hotKey="mod+enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{ description: "Save" }}
                >
                    Save
                </ButtonWithLoader>
            </HStack>
        </HStack>
    );
};
